import { eventById } from "../../../../engine/scoring";
import { displayName, nemesis, playerGames, roundByNo, streak } from "../../../../engine/selectors";
import type { PlayerId, TournamentState } from "../../../../engine/types";

/**
 * My games, newest first, one row each: the round and court on the court's colour, who I played
 * with and against, then the score on a block that says how it went. Above them the last five
 * and, once I have one, my nemesis.
 */
export function MyGames({ state, me }: { state: TournamentState; me: PlayerId }) {
  const games = playerGames(state, me);
  const name = (id: PlayerId): string => displayName(state.players[id], "phone");
  const form = streak(games);
  const foe = nemesis(state, me);
  const live = state.clock.phase === "playing" ? state.clock.roundNo : null;
  return (
    <section className="pn-games">
      <h2>My games</h2>
      {!games.length && <p className="pn-empty">No games yet. Each one shows here as you play it.</p>}
      {form && (
        <p className="pn-streak">
          <span>Last five</span>
          {/* newest first, as the list below */}
          {form
            .split(" ")
            .reverse()
            .map((r, i) => (
            <b key={i} className={r === "W" ? "won" : "lost"}>
              {r}
            </b>
          ))}
        </p>
      )}
      {foe && (
        <p className="pn-nemesis">
          Nemesis: {name(foe.id)}. Played {foe.played}, lost {foe.lost}.
        </p>
      )}
      {games.length > 0 && (
        <ol>
          {[...games].reverse().map((g) => {
            const event = eventById(state.config, g.eventId);
            let result: string;
            let kind: string;
            if (g.voided) [result, kind] = ["void", "void"];
            else if (g.score) {
              // a game the clock ended says so: 9-7 is not a finished game to 11
              const capped = roundByNo(state, g.roundNo)?.games.find((x) => x.court === g.court)?.score?.capped;
              [result, kind] = [`${g.score[0]}-${g.score[1]} ${g.won ? "W" : "L"}${capped ? ", time" : ""}`, g.won ? "won" : "lost"];
            }
            else if (g.roundNo === live) [result, kind] = ["now", "now"];
            else [result, kind] = ["owed", "owed"];
            return (
              <li key={g.roundNo} className={`pn-game court-${g.court}`}>
                <span className="pn-game-where">
                  <strong>R{g.roundNo}</strong>
                  <span>court {g.court}</span>
                </span>
                <span className="pn-game-who">
                  <span>with {name(g.partner)}</span>
                  <span>
                    v {g.opponents.map(name).join(" & ")}
                    {event ? ` · ${event.name}` : ""}
                  </span>
                </span>
                <span className={`pn-game-result ${kind}`}>{result}</span>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
